import { useState } from 'react'
import { DentalChart } from './DentalChart'
import { cn } from '@/lib/utils'

/**
 * Multi-grade tooth selector.
 *
 * `value` shape:
 *   { present: bool, grade_1: AffectedTeeth | null, grade_2: AffectedTeeth | null, ... }
 *
 * `grades` prop: array of grade keys, e.g. ['grade_1', ..., 'grade_6'] for caries
 * or ['grade_1', 'grade_2', 'grade_3'] for mobility.
 *
 * Enforces "one tooth, one grade" — when you click a tooth, it's removed from
 * any other grade and added to the active grade.
 */

const GRADE_COLORS = {
  grade_1: 'bg-emerald-500 text-white ring-emerald-600',
  grade_2: 'bg-lime-500 text-ink-900 ring-lime-600',
  grade_3: 'bg-amber-500 text-ink-900 ring-amber-600',
  grade_4: 'bg-orange-500 text-white ring-orange-600',
  grade_5: 'bg-rose-500 text-white ring-rose-600',
  grade_6: 'bg-rose-700 text-white ring-rose-800',
}

const GRADE_LABEL = (k) => `Grade ${k.split('_')[1]}`

function emptyAffected() {
  return { adult_teeth: [], child_teeth: [] }
}

export function GradedToothSelector({
  label,
  value,
  onChange,
  grades = ['grade_1', 'grade_2', 'grade_3'],
  hint,
}) {
  const v = value || { present: false }
  const [activeGrade, setActiveGrade] = useState(grades[0])
  const [mode, setMode] = useState('adult')

  const setPresent = (b) => {
    if (!b) {
      // Clear all grade data when turning off
      const cleared = { present: false }
      grades.forEach((g) => (cleared[g] = null))
      onChange?.(cleared)
    } else {
      onChange?.({ ...v, present: true })
    }
  }

  // Compute which grade owns each tooth (for color rendering)
  const ownerOf = (n, dentition) => {
    for (const g of grades) {
      const teeth = v[g]?.[dentition === 'adult' ? 'adult_teeth' : 'child_teeth']
      if (Array.isArray(teeth) && teeth.includes(n)) return g
    }
    return null
  }

  const getToothColor = (n, dentition) => {
    const owner = ownerOf(n, dentition)
    if (!owner) return null
    // Active grade gets a stronger ring
    const base = GRADE_COLORS[owner] || 'bg-primary-700 text-cream ring-primary-800'
    return cn(
      base,
      owner === activeGrade ? 'shadow-soft ring-2' : 'opacity-90',
    )
  }

  // Custom change handler: click → ensure tooth lives only in activeGrade
  const handleChartChange = (next) => {
    // `next` is the merged adult_teeth/child_teeth across ALL grades, but
    // DentalChart only knows about the active grade because we feed it that.
    // We adapt: compute the diff to know what was added/removed.
    const cur = v[activeGrade] || emptyAffected()
    const addedAdult = next.adult_teeth.filter((n) => !cur.adult_teeth.includes(n))
    const removedAdult = cur.adult_teeth.filter((n) => !next.adult_teeth.includes(n))
    const addedChild = next.child_teeth.filter((n) => !cur.child_teeth.includes(n))
    const removedChild = cur.child_teeth.filter((n) => !next.child_teeth.includes(n))

    // Start from current state with active grade replaced
    const result = { ...v, present: true, [activeGrade]: next }

    // For each newly-added tooth, remove it from any other grade
    for (const g of grades) {
      if (g === activeGrade) continue
      const existing = result[g]
      if (!existing) continue
      const cleanedAdult = (existing.adult_teeth || []).filter((n) => !addedAdult.includes(n))
      const cleanedChild = (existing.child_teeth || []).filter((n) => !addedChild.includes(n))
      const cleaned = { adult_teeth: cleanedAdult, child_teeth: cleanedChild }
      result[g] =
        cleanedAdult.length === 0 && cleanedChild.length === 0 ? null : cleaned
    }

    // If the active grade is now empty, set to null
    if (
      result[activeGrade].adult_teeth.length === 0 &&
      result[activeGrade].child_teeth.length === 0
    ) {
      result[activeGrade] = null
    }

    onChange?.(result)
  }

  // What to feed the DentalChart — only the active grade's selection
  const activeValue = v[activeGrade] || emptyAffected()

  return (
    <div className="w-full">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink-800">{label}</p>
        <div className="inline-flex rounded-xl bg-ink-100/70 p-1 ring-1 ring-ink-200">
          {[
            { v: false, label: 'No' },
            { v: true, label: 'Yes' },
          ].map((opt) => (
            <button
              key={String(opt.v)}
              type="button"
              onClick={() => setPresent(opt.v)}
              className={cn(
                'rounded-lg px-3 py-1 text-xs font-bold transition-all',
                v.present === opt.v
                  ? 'bg-white text-primary-800 shadow-soft ring-1 ring-ink-200'
                  : 'text-ink-600 hover:text-ink-900',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {hint && <p className="mb-3 text-xs text-ink-500">{hint}</p>}

      {v.present && (
        <div className="space-y-3">
          {/* Grade tabs */}
          <div className="flex flex-wrap gap-1.5">
            {grades.map((g) => {
              const isActive = g === activeGrade
              const count =
                (v[g]?.adult_teeth?.length || 0) + (v[g]?.child_teeth?.length || 0)
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setActiveGrade(g)}
                  className={cn(
                    'flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all',
                    'ring-1 ring-inset',
                    isActive
                      ? cn(GRADE_COLORS[g], 'shadow-soft')
                      : 'bg-white text-ink-700 ring-ink-200 hover:ring-ink-300',
                  )}
                >
                  <span
                    className={cn(
                      'h-2.5 w-2.5 rounded-full',
                      GRADE_COLORS[g]?.split(' ')[0] || 'bg-primary-700',
                    )}
                  />
                  {GRADE_LABEL(g)}
                  {count > 0 && (
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                        isActive ? 'bg-white/25' : 'bg-ink-100 text-ink-700',
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <p className="text-xs text-ink-500">
            Tap teeth to assign them to{' '}
            <span className="font-semibold text-ink-700">{GRADE_LABEL(activeGrade)}</span>.
            Teeth already in another grade will move over.
          </p>

          <DentalChart
            value={activeValue}
            onChange={handleChartChange}
            mode={mode}
            showToggle={false}
            getToothColor={getToothColor}
          />

          {/* Dentition toggle (custom, since we passed mode to DentalChart) */}
          <div className="inline-flex rounded-xl bg-ink-100/70 p-1 ring-1 ring-ink-200">
            {[
              { v: 'adult', label: 'Adult (32)' },
              { v: 'child', label: 'Child (20)' },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setMode(opt.v)}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all',
                  mode === opt.v
                    ? 'bg-white text-primary-800 shadow-soft ring-1 ring-ink-200'
                    : 'text-ink-600 hover:text-ink-900',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
